/**
 * Translation service with Firebase caching.
 * Uses LibreTranslate for ES↔EN and MyMemory for Latin.
 * Caches results in the text node to avoid re-requesting.
 */

const LIBRETRANSLATE_URL = "https://libretranslate.de/translate";
const MYMEMORY_URL = "https://api.mymemory.translated.net/get";

// LibreTranslate supports these well
async function translateLibre(text, source, target) {
  const res = await fetch(LIBRETRANSLATE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      q: text,
      source,
      target,
      format: "html",
    }),
  });
  if (!res.ok) throw new Error(`LibreTranslate error: ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.translatedText;
}

// MyMemory supports Latin and many others
async function translateMyMemory(text, source, target) {
  const pair = `${source}|${target}`;
  const url = `${MYMEMORY_URL}?q=${encodeURIComponent(text)}&langpair=${pair}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`MyMemory error: ${res.status}`);
  const data = await res.json();
  if (data.responseStatus !== 200) {
    throw new Error(data.responseDetails || "MyMemory translation failed");
  }
  return data.responseData.translatedText;
}

/**
 * Translate text. Tries LibreTranslate first for modern languages,
 * falls back to MyMemory for Latin or on failure.
 */
export async function translateText(text, source, target) {
  if (!text || !text.trim()) return "";
  if (source === target) return text;

  const needsLatin = source === "la" || target === "la";

  try {
    if (!needsLatin) {
      return await translateLibre(text, source, target);
    }
  } catch (err) {
    console.warn("LibreTranslate failed, trying MyMemory:", err.message);
  }

  // Fallback: MyMemory (handles Latin and acts as fallback)
  return await translateMyMemory(text, source, target);
}

/**
 * Batch-translate title + content for a text.
 * Returns { title, content } in the target language.
 */
export async function translateArticle(title, content, source, target) {
  const [translatedTitle, translatedContent] = await Promise.all([
    translateText(title, source, target),
    translateText(content, source, target),
  ]);
  return { title: translatedTitle, content: translatedContent };
}

/**
 * Get the best source language for a text.
 * Default assumption is Spanish since the app is in Spanish.
 */
export function detectSourceLang(text) {
  if (!text) return "es";
  // Very naive heuristic: if it contains common Spanish words/patterns
  const spanishMarkers = /\b(el|la|los|las|de|del|que|en|un|una|y|es|son|por|con|para)\b/gi;
  const matches = text.match(spanishMarkers);
  if (matches && matches.length >= 3) return "es";
  return "en"; // default fallback
}
